import { Table } from '@/components/organisms/table';
import { Skeleton, TableRow } from '@mui/material';
import { StyledTableCell } from '@/components/organisms/table/styles';
import { useEffect, useMemo, useState, ReactNode, useRef } from 'react';
import { useGeometry } from '@/hooks/useGeometry';
import { useApolloClient } from '@apollo/client';
import { QUERY_GEOMETRY_FOR_TABLE } from '@/graphql/queries/geometry';
import { StyledTableContainer } from './styles';
import { sort } from '@/utils/functions/sort';
import { AppRouters } from '@/utils/constants/routers';
import { useNavigate } from 'react-router-dom';
import { DimensionScore } from '@/graphql/types/dimensions';
import { useTranslation } from '@/hooks/useTranslation';
import {
  ClassificationOpportunityAndRisk as ClassificationOpportunityAndRiskType,
  ClassificationOpportunityAndRisk,
} from '@/types/classification';
import { useLanguage } from '@/hooks/useLanguage';
import { SafeMarkdown } from '@/components/atoms/markdown/safe';

interface TableData {
  municipality_code: string;
  code_uf: string;
  name_municipality: string;
  name_uf: string;
  municipality_score: {
    general_score_risk: number;
    general_score_opportunity: number;
    classification_risk: ClassificationOpportunityAndRisk;
    classification_opportunity: ClassificationOpportunityAndRisk;
    intensity_color_opportunity: string;
    intensity_color_risk: string;
  };
  dimensions_scores: DimensionScore[];
}

interface ScoreTableProps {
  variant: 'risk' | 'opportunity';
  customPagination?: ReactNode;
}

interface DimensionHeader {
  dimension: {
    id: number | string;
    name: string;
  };
}

export const ScoreTable = ({ variant, customPagination }: ScoreTableProps) => {
  const apolloClient = useApolloClient();
  const navigate = useNavigate();

  const { filter } = useGeometry();
  const { t } = useTranslation();
  const { currentLanguage } = useLanguage();

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [tableData, setTableData] = useState<TableData[]>([]);
  const [tableTotal, setTableTotal] = useState(0);
  const [order, setOrder] = useState<'ASC' | 'DESC'>('ASC');
  const [orderBy, setOrderBy] = useState('name_municipality');
  const [isLoading, setIsLoading] = useState(true);

  // Refs to preserve horizontal scroll position across sorts
  const outerContainerRef = useRef<HTMLDivElement | null>(null);
  const lastScrollLeftRef = useRef<number>(0);
  const shouldRestoreScrollRef = useRef<boolean>(false);

  const customOrder = [1, 2, 3, 4, 5, 6, 7, 8];

  const captureHorizontalScroll = () => {
    const scrollContainer = outerContainerRef.current?.querySelector(
      '.MuiTableContainer-root'
    ) as HTMLElement | null;
    if (scrollContainer) {
      lastScrollLeftRef.current = scrollContainer.scrollLeft;
      shouldRestoreScrollRef.current = true;
    }
  };

  const byOpportunityAndRisk = (
    classification: ClassificationOpportunityAndRiskType,
    variant: 'opportunity' | 'risk'
  ) => {
    switch (classification) {
      case ClassificationOpportunityAndRisk.LOW:
        return variant === 'opportunity'
          ? t('label.label.low_opportunity')
          : t('label.label.low_risk');
      case ClassificationOpportunityAndRisk.MEDIUM_LOW:
        return variant === 'opportunity'
          ? t('label.label.medium_low_opportunity')
          : t('label.label.medium_low_risk');
      case ClassificationOpportunityAndRisk.MEDIUM_HIGH:
        return variant === 'opportunity'
          ? t('label.label.medium_high_opportunity')
          : t('label.label.medium_high_risk');
      default:
        return variant === 'opportunity'
          ? t('label.label.high_opportunity')
          : t('label.label.high_risk');
    }
  };

  const getOrderedDimensions = useMemo(
    () => (data: TableData[]): DimensionHeader[] => {
      if (!data.length)
        return [
          {
            dimension: {
              id: 'municipality',
              name: t('label.label.municipality'),
            },
          },
        ];

      const staticHeaders: DimensionHeader[] = [
        {
          dimension: {
            id: 'municipality',
            name: t('label.label.municipality'),
          },
        },
        { dimension: { id: 'state', name: t('label.label.state') } },
        {
          dimension: {
            id: 'classification',
            name: t('label.label.classification'),
          },
        },
        {
          dimension: {
            id: 'score',
            name:
              variant === 'risk' ? t('label.label.risk_level') : t('label.label.opportunity_level'),
          },
        },
      ];

      const uniqueDimensions = new Map<number, DimensionScore>();
      data.forEach((city) => {
        city.dimensions_scores.forEach((d) => {
          uniqueDimensions.set(d.dimension.id, d);
        });
      });

      const orderedDimensionScores = sort.sortDimensions(
        Array.from(uniqueDimensions.values()) || [],
        customOrder
      );

      // Filter dimensions based on variant
      const filteredDimensionScores = orderedDimensionScores.filter((dimension) => {
        if (variant === 'risk' && dimension.dimension.bigquery_id === 'economica') {
          return false;
        }
        if (variant === 'opportunity' && dimension.dimension.bigquery_id === 'seguranca') {
          return false;
        }
        return true;
      });

      return [...staticHeaders, ...filteredDimensionScores];
    },
    [variant, t]
  );

  const headers = getOrderedDimensions(tableData);

  const orderNormalized = order === 'ASC' ? 'asc' : 'desc';

  const handleRequestSort = (property: string) => {
    // Capture current horizontal scroll before triggering a re-render/fetch
    captureHorizontalScroll();

    const isAsc = orderBy === property && order === 'ASC';
    setOrder(isAsc ? 'DESC' : 'ASC');
    setOrderBy(property);
  };

  const getTableData = async () => {
    // Capture scroll so it can be restored after data loads (pagination, rows change, variant change)
    captureHorizontalScroll();
    setIsLoading(true);
    try {
      const { data } = await apolloClient.query<{
        advancedfilteredMunicipalities: {
          total: number;
          data: TableData[];
        };
      }>({
        query: QUERY_GEOMETRY_FOR_TABLE,
        variables: {
          filters: {
            ...filter,
            municipality: undefined,
            order,
            orderBy,
            orderType: variant,
          },
          pagination: { page: page + 1, limit: rowsPerPage },
          language: currentLanguage,
        },
        fetchPolicy: 'no-cache',
      });
      setTableData(data.advancedfilteredMunicipalities.data);
      setTableTotal(data.advancedfilteredMunicipalities.total);
    } catch (error) {
      console.error('Error fetching filtered polygons:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const getData = async () => {
      await getTableData();
    };
    getData();
  }, [page, rowsPerPage, filter, order, orderBy, variant, currentLanguage]);

  // Restore horizontal scroll after loading completes, if needed
  useEffect(() => {
    if (!isLoading && shouldRestoreScrollRef.current) {
      const scrollContainer = outerContainerRef.current?.querySelector(
        '.MuiTableContainer-root'
      ) as HTMLElement | null;
      if (scrollContainer) {
        // Use rAF to ensure DOM has painted before restoring
        requestAnimationFrame(() => {
          scrollContainer.scrollLeft = lastScrollLeftRef.current;
        });
      }
      shouldRestoreScrollRef.current = false;
    }
  }, [isLoading]);

  return (
    <StyledTableContainer ref={outerContainerRef}>
      <Table
        headers={headers.map((header) => {
          return {
            id: sort.valuesToSortTable({
              header: header.dimension.name,
              id: header.dimension.id,
              variant,
            }),
            label: header.dimension.name,
            sortable: true,
          };
        })}
        pinnedColumn={headers[0].dimension.name}
        rowsLabel={t('table.table.municipality.rows_per_page')}
        listTotal={tableTotal}
        page={page}
        rowsPerPage={rowsPerPage}
        onGetPage={(newPage) => {
          captureHorizontalScroll();
          setPage(newPage);
        }}
        onGetRowsPage={(newRowsPerPage) => {
          captureHorizontalScroll();
          setRowsPerPage(newRowsPerPage);
        }}
        onRequestSort={handleRequestSort}
        order={orderNormalized}
        orderBy={orderBy}
        showPagination
        customPagination={customPagination}
      >
        {isLoading
          ? Array.from({ length: rowsPerPage }).map((_, rowIndex) => (
              <TableRow key={`skeleton-row-${rowIndex}`}>
                {headers.map((_, colIndex) => (
                  <StyledTableCell key={`skeleton-cell-${rowIndex}-${colIndex}`}>
                    <Skeleton width="100%" variant="text" animation="wave" />
                  </StyledTableCell>
                ))}
              </TableRow>
            ))
          : tableData?.map((item, index) => (
              <TableRow key={index}>
                <StyledTableCell
                  className="sticky-col"
                  isLink
                  onClick={() => {
                    sessionStorage.removeItem('URL_FILTER');
                    sessionStorage.setItem('URL_FILTER', JSON.stringify(window.location.pathname));
                    navigate(`${AppRouters.municipality}/${item.municipality_code}`, {
                      state: { name_municipality: item.name_municipality },
                    });
                  }}
                >
                  <SafeMarkdown text={item.name_municipality} />
                </StyledTableCell>

                <StyledTableCell>
                  <SafeMarkdown text={item.name_uf} />
                </StyledTableCell>

                <StyledTableCell>
                  <SafeMarkdown
                    text={
                      variant === 'opportunity'
                        ? byOpportunityAndRisk(
                            item.municipality_score.classification_opportunity,
                            'opportunity'
                          )
                        : byOpportunityAndRisk(item.municipality_score.classification_risk, 'risk')
                    }
                  />
                </StyledTableCell>
                <StyledTableCell>
                  {variant === 'risk'
                    ? item.municipality_score.general_score_risk
                    : item.municipality_score.general_score_opportunity}
                </StyledTableCell>

                {item.dimensions_scores
                  .filter((dimension) => {
                    // Hide "economica" dimension when variant is "risk"
                    if (variant === 'risk' && dimension.dimension.bigquery_id === 'economica') {
                      return false;
                    }
                    // Hide "seguranca" dimension when variant is "opportunity"
                    if (
                      variant === 'opportunity' &&
                      dimension.dimension.bigquery_id === 'seguranca'
                    ) {
                      return false;
                    }
                    return true;
                  })
                  .map((dimension) => (
                    <StyledTableCell key={dimension.dimension.id}>
                      {variant === 'risk' ? dimension.score_risk : dimension.score_opportunity}
                    </StyledTableCell>
                  ))}
              </TableRow>
            ))}
      </Table>
    </StyledTableContainer>
  );
};
